// src/components/Login.js - גרסה מעודכנת עם הודעות משופרות
import React, { useState } from 'react';
import { 
  Input, 
  Button, 
  Heading, 
  FormControl, 
  FormLabel, 
  InputGroup, 
  InputRightElement, 
  useToast, 
  Spinner, 
  Center,
  Card,
  CardBody,
  CardHeader,
  VStack,
  Text,
  useColorModeValue,
  Icon,
  Box,
  Alert,
  AlertIcon,
  AlertDescription
} from '@chakra-ui/react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { ViewIcon, ViewOffIcon, LockIcon } from '@chakra-ui/icons';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // צבעים עבור Dark/Light mode
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "שגיאה",
        description: "אנא מלא את כל השדות",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // בדיקת פורמט אימייל בסיסי
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "שגיאה",
        description: "פורמט האימייל לא תקין",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      console.log('Attempting to sign in user:', email);
      await signInWithEmailAndPassword(auth, email, password);
      
      toast({
        title: "התחברות הצליחה! 🎉",
        description: "מכין את המערכת עבורך... ברוכים הבאים!",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      
      console.log('User signed in successfully:', email);
    } catch (error) {
      console.error("Error logging in:", error);
      
      let errorMessage = "שם משתמש או סיסמה לא נכונים";
      let errorDescription = "";
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = "משתמש לא קיים במערכת";
          errorDescription = "בדוק את כתובת האימייל או פנה למנהל המערכת";
          break;
        case 'auth/wrong-password':
          errorMessage = "סיסמה שגויה";
          errorDescription = "בדוק את הסיסמה ונסה שוב";
          break;
        case 'auth/too-many-requests':
          errorMessage = "יותר מידי ניסיונות התחברות";
          errorDescription = "נסה שוב בעוד מספר דקות";
          break;
        case 'auth/user-disabled':
          errorMessage = "חשבון המשתמש הושבת";
          errorDescription = "פנה למנהל המערכת";
          break;
        case 'auth/invalid-email':
          errorMessage = "כתובת אימייל לא תקינה";
          errorDescription = "בדוק את פורמט האימייל";
          break;
        case 'auth/network-request-failed':
          errorMessage = "בעיית חיבור לרשת";
          errorDescription = "בדוק את החיבור לאינטרנט ונסה שוב";
          break;
        default:
          errorMessage = "שגיאה בהתחברות";
          errorDescription = "אירעה שגיאה לא צפויה. נסה שוב.";
      }
      
      toast({
        title: errorMessage,
        description: errorDescription,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Center h="100vh">
      <Card 
        variant="outline" 
        borderColor={borderColor}
        bg={bgColor}
        boxShadow="xl"
        width="100%"
        maxW="md"
      >
        <CardHeader textAlign="center">
          <VStack spacing={3}>
            <Icon as={LockIcon} boxSize={12} color="teal.500" />
            <Heading size="lg" color="teal.600">
              כניסה למערכת
            </Heading>
            <Text color="gray.600" fontSize="sm">
              מערכת ניהול שיחות מתקדמת
            </Text>
          </VStack>
        </CardHeader>
        
        <CardBody>
          <form onSubmit={handleLogin}>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel>כתובת אימייל</FormLabel>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  focusBorderColor="teal.400"
                  size="lg"
                  disabled={loading}
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>סיסמה</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="הכנס סיסמה"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    focusBorderColor="teal.400"
                    size="lg"
                    disabled={loading}
                  />
                  <InputRightElement width="4.5rem" height="3rem">
                    <Button 
                      h="2rem" 
                      size="sm" 
                      onClick={togglePasswordVisibility}
                      variant="ghost"
                      disabled={loading}
                    >
                      {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              
              <Button 
                type="submit" 
                colorScheme="teal" 
                isDisabled={loading || !email || !password}
                width="100%"
                size="lg"
                isLoading={loading}
                loadingText="מתחבר..."
              >
                התחבר
              </Button>
            </VStack>
          </form>
          
          {loading && (
            <Box mt={6}>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  מתחבר למערכת ומכין את הפרופיל שלך...
                </AlertDescription>
              </Alert>
            </Box>
          )}
          
          <Box mt={6}>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontWeight="bold">טיפים להתחברות:</Text>
                <Text fontSize="sm">• ודא שהאימייל והסיסמה נכונים</Text>
                <Text fontSize="sm">• אם אתה משתמש חדש, מנהל המערכת צריך ליצור לך חשבון</Text>
                <Text fontSize="sm">• לבעיות טכניות, פנה למנהל המערכת</Text>
              </VStack>
            </Alert>
          </Box>
        </CardBody>
      </Card>
    </Center>
  );
};

export default Login;